import {
  readUiSettingStorageValue,
  useUiSettingStorageListener,
  writeUiSettingStorageValue,
} from '@affine/core/modules/ui-settings-vault';
import { ViewBody, ViewHeader } from '@affine/core/modules/workbench';
import { CloseIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import {
  type ComponentProps,
  type CSSProperties,
  forwardRef,
  Fragment,
  type HTMLAttributes,
  type PointerEventHandler,
  type ReactElement,
  type ReactNode,
  type TransitionEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  WorkspaceAppSidebarFrame,
  WorkspaceAppSidebarToggleButton,
} from '../../../components/workspace-app-sidebar';
import { STREAMLAIN_THREE_PANE_COMPACT_QUERY } from '../shared/workspace-pane-layout';
import * as styles from './native-workspace-app.css';

export const NATIVE_WORKSPACE_APP_DEFAULT_LEADING_PANE_WIDTH = 220;
export const NATIVE_WORKSPACE_APP_DEFAULT_PRIMARY_PANE_WIDTH = 320;
export const NATIVE_WORKSPACE_APP_MIN_LEADING_PANE_WIDTH = 180;
export const NATIVE_WORKSPACE_APP_MAX_LEADING_PANE_WIDTH = 360;
export const NATIVE_WORKSPACE_APP_MIN_PRIMARY_PANE_WIDTH = 260;
export const NATIVE_WORKSPACE_APP_MAX_PRIMARY_PANE_WIDTH = 560;
export const NATIVE_WORKSPACE_APP_PANE_WIDTHS_STORAGE_PREFIX =
  'streamlain.nativeWorkspaceApp.paneWidths.v1';
export const NATIVE_WORKSPACE_APP_SIDEBAR_VISIBILITY_STORAGE_PREFIX =
  'streamlain.nativeWorkspaceApp.sidebarVisibility.v1';

const NATIVE_WORKSPACE_APP_RESIZE_SETTLE_MS = 220;
const NATIVE_WORKSPACE_APP_SIDEBAR_TRANSITION_MS = 280;

export type NativeWorkspaceAppSidebarTarget =
  | 'custom-app'
  | 'mail'
  | 'planner'
  | 'workspace';

export const NATIVE_WORKSPACE_APP_SIDEBAR_EVENT =
  'streamlain:native-workspace-app-sidebar';

const matchesNativeWorkspaceAppViewport = (query: string) =>
  typeof window !== 'undefined' && window.matchMedia(query).matches;

export function useNativeWorkspaceAppCompactViewport(): boolean {
  const [matches, setMatches] = useState(() =>
    matchesNativeWorkspaceAppViewport(STREAMLAIN_THREE_PANE_COMPACT_QUERY)
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(STREAMLAIN_THREE_PANE_COMPACT_QUERY);
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return matches;
}

export function requestNativeWorkspaceAppSidebar(
  target: NativeWorkspaceAppSidebarTarget,
  action: 'open' | 'toggle' = 'toggle'
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(NATIVE_WORKSPACE_APP_SIDEBAR_EVENT, {
      detail: { action, target },
    })
  );
}

export function useNativeWorkspaceAppSidebarVisibility(
  target: NativeWorkspaceAppSidebarTarget,
  {
    defaultOpen = true,
    storageKey,
  }: {
    defaultOpen?: boolean;
    storageKey?: string | null;
  } = {}
): readonly [boolean, (open: boolean) => void] {
  const stableStorageKey = useMemo(
    () => normalizedStorageKey(storageKey),
    [storageKey]
  );
  const readStoredOpen = useCallback((): boolean | null => {
    if (!stableStorageKey) return null;
    try {
      const raw = readUiSettingStorageValue(
        stableStorageKey,
        NATIVE_WORKSPACE_APP_PANE_WIDTH_STORAGE_OPTIONS
      );
      return raw === 'true' ? true : raw === 'false' ? false : null;
    } catch {
      return null;
    }
  }, [stableStorageKey]);
  const [open, setOpenState] = useState(() => readStoredOpen() ?? defaultOpen);
  const openRef = useRef(open);

  useEffect(() => {
    const nextOpen = readStoredOpen() ?? defaultOpen;
    openRef.current = nextOpen;
    setOpenState(nextOpen);
  }, [defaultOpen, readStoredOpen]);

  const setOpen = useCallback(
    (open: boolean) => {
      openRef.current = open;
      setOpenState(open);
      if (!stableStorageKey) return;
      try {
        writeUiSettingStorageValue(
          stableStorageKey,
          String(open),
          NATIVE_WORKSPACE_APP_PANE_WIDTH_STORAGE_OPTIONS
        );
      } catch {
        // Sidebar visibility should never break app rendering.
      }
    },
    [stableStorageKey]
  );

  useEffect(() => {
    const handleRequest = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          action?: 'open' | 'toggle';
          target?: NativeWorkspaceAppSidebarTarget;
        }>
      ).detail;
      if (detail?.target !== target) return;
      setOpen(detail.action === 'open' ? true : !openRef.current);
    };

    window.addEventListener(NATIVE_WORKSPACE_APP_SIDEBAR_EVENT, handleRequest);
    return () =>
      window.removeEventListener(
        NATIVE_WORKSPACE_APP_SIDEBAR_EVENT,
        handleRequest
      );
  }, [setOpen, target]);

  return [open, setOpen] as const;
}

const NATIVE_WORKSPACE_APP_PANE_WIDTH_STORAGE_OPTIONS = {
  sync: 'local-only',
} as const;

export interface NativeWorkspaceAppPaneWidths {
  leading: number;
  primary: number;
}

type NativeWorkspaceAppPaneWidthsUpdater =
  | NativeWorkspaceAppPaneWidths
  | ((current: NativeWorkspaceAppPaneWidths) => NativeWorkspaceAppPaneWidths);

const DEFAULT_NATIVE_WORKSPACE_APP_PANE_WIDTHS: NativeWorkspaceAppPaneWidths = {
  leading: NATIVE_WORKSPACE_APP_DEFAULT_LEADING_PANE_WIDTH,
  primary: NATIVE_WORKSPACE_APP_DEFAULT_PRIMARY_PANE_WIDTH,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function clampPaneWidth(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizePaneWidth(
  value: unknown,
  fallback: number,
  min: number,
  max: number
): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? clampPaneWidth(Math.round(value), min, max)
    : fallback;
}

function normalizeNativeWorkspaceAppPaneWidths(
  value: unknown
): NativeWorkspaceAppPaneWidths {
  if (!isRecord(value)) return DEFAULT_NATIVE_WORKSPACE_APP_PANE_WIDTHS;

  return {
    leading: normalizePaneWidth(
      value['leading'],
      NATIVE_WORKSPACE_APP_DEFAULT_LEADING_PANE_WIDTH,
      NATIVE_WORKSPACE_APP_MIN_LEADING_PANE_WIDTH,
      NATIVE_WORKSPACE_APP_MAX_LEADING_PANE_WIDTH
    ),
    primary: normalizePaneWidth(
      value['primary'],
      NATIVE_WORKSPACE_APP_DEFAULT_PRIMARY_PANE_WIDTH,
      NATIVE_WORKSPACE_APP_MIN_PRIMARY_PANE_WIDTH,
      NATIVE_WORKSPACE_APP_MAX_PRIMARY_PANE_WIDTH
    ),
  };
}

function readNativeWorkspaceAppPaneWidths(
  storageKey: string | null
): NativeWorkspaceAppPaneWidths {
  if (!storageKey) return DEFAULT_NATIVE_WORKSPACE_APP_PANE_WIDTHS;

  try {
    const raw = readUiSettingStorageValue(
      storageKey,
      NATIVE_WORKSPACE_APP_PANE_WIDTH_STORAGE_OPTIONS
    );
    if (!raw) return DEFAULT_NATIVE_WORKSPACE_APP_PANE_WIDTHS;
    return normalizeNativeWorkspaceAppPaneWidths(JSON.parse(raw) as unknown);
  } catch {
    return DEFAULT_NATIVE_WORKSPACE_APP_PANE_WIDTHS;
  }
}

function writeNativeWorkspaceAppPaneWidths(
  storageKey: string | null,
  paneWidths: NativeWorkspaceAppPaneWidths
): void {
  if (!storageKey) return;

  try {
    writeUiSettingStorageValue(
      storageKey,
      JSON.stringify(normalizeNativeWorkspaceAppPaneWidths(paneWidths)),
      NATIVE_WORKSPACE_APP_PANE_WIDTH_STORAGE_OPTIONS
    );
  } catch {
    // Pane widths should never break app rendering in private/quota contexts.
  }
}

function encodeStorageScopePart(value: string): string {
  return encodeURIComponent(value.trim());
}

function normalizedStorageKey(
  storageKey: string | null | undefined
): string | null {
  const normalized = storageKey?.trim();
  return normalized ? normalized : null;
}

export function createNativeWorkspaceAppPaneWidthsStorageKey({
  appId,
  workspaceId,
}: {
  appId: string | null | undefined;
  workspaceId: string | null | undefined;
}): string | null {
  const normalizedAppId = appId?.trim();
  const normalizedWorkspaceId = workspaceId?.trim();
  if (!normalizedAppId || !normalizedWorkspaceId) return null;

  return `${NATIVE_WORKSPACE_APP_PANE_WIDTHS_STORAGE_PREFIX}:app:${encodeStorageScopePart(
    normalizedAppId
  )}:workspace:${encodeStorageScopePart(normalizedWorkspaceId)}`;
}

export function createNativeWorkspaceAppSidebarVisibilityStorageKey({
  appId,
  workspaceId,
}: {
  appId: string | null | undefined;
  workspaceId: string | null | undefined;
}): string | null {
  const normalizedAppId = appId?.trim();
  const normalizedWorkspaceId = workspaceId?.trim();
  if (!normalizedAppId || !normalizedWorkspaceId) return null;

  return `${NATIVE_WORKSPACE_APP_SIDEBAR_VISIBILITY_STORAGE_PREFIX}:app:${encodeStorageScopePart(
    normalizedAppId
  )}:workspace:${encodeStorageScopePart(normalizedWorkspaceId)}`;
}

export function createNativeWorkspaceAppPaneWidthsStyle(
  paneWidths: NativeWorkspaceAppPaneWidths
): CSSProperties {
  const normalized = normalizeNativeWorkspaceAppPaneWidths(paneWidths);
  return {
    '--native-app-leading-pane-width': `${normalized.leading}px`,
    '--native-app-sidebar-overlay-width': '320px',
    '--native-app-sidebar-width': `${normalized.leading}px`,
    '--native-app-primary-pane-width': `${normalized.primary}px`,
  } as CSSProperties;
}

export function useNativeWorkspaceAppPaneWidths(
  storageKey: string | null | undefined
) {
  const stableStorageKey = useMemo(
    () => normalizedStorageKey(storageKey),
    [storageKey]
  );
  const [paneWidths, setPaneWidthsState] =
    useState<NativeWorkspaceAppPaneWidths>(() =>
      readNativeWorkspaceAppPaneWidths(stableStorageKey)
    );

  useEffect(() => {
    setPaneWidthsState(readNativeWorkspaceAppPaneWidths(stableStorageKey));
  }, [stableStorageKey]);

  useUiSettingStorageListener(
    stableStorageKey,
    NATIVE_WORKSPACE_APP_PANE_WIDTH_STORAGE_OPTIONS,
    useCallback(rawValue => {
      try {
        setPaneWidthsState(
          normalizeNativeWorkspaceAppPaneWidths(JSON.parse(rawValue) as unknown)
        );
      } catch {
        setPaneWidthsState(DEFAULT_NATIVE_WORKSPACE_APP_PANE_WIDTHS);
      }
    }, [])
  );

  const setPaneWidths = useCallback(
    (next: NativeWorkspaceAppPaneWidthsUpdater) => {
      setPaneWidthsState(current => {
        const value = (() => {
          if (typeof next !== 'function') return next;
          const update = next as (
            current: NativeWorkspaceAppPaneWidths
          ) => NativeWorkspaceAppPaneWidths;
          return update(current);
        })();
        const normalized = normalizeNativeWorkspaceAppPaneWidths(value);
        writeNativeWorkspaceAppPaneWidths(stableStorageKey, normalized);
        return normalized;
      });
    },
    [stableStorageKey]
  );

  return [paneWidths, setPaneWidths] as const;
}

export function useNativeWorkspaceAppPrimaryPaneController(
  storageKey: string | null | undefined,
  {
    maxWidth = NATIVE_WORKSPACE_APP_MAX_PRIMARY_PANE_WIDTH,
    minWidth = NATIVE_WORKSPACE_APP_MIN_PRIMARY_PANE_WIDTH,
  }: {
    maxWidth?: number;
    minWidth?: number;
  } = {}
) {
  const [paneWidths, setPaneWidths] =
    useNativeWorkspaceAppPaneWidths(storageKey);
  const startPrimaryPaneResize = useCallback<
    PointerEventHandler<HTMLDivElement>
  >(
    event => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = paneWidths.primary;
      const onPointerMove = (moveEvent: PointerEvent) => {
        const nextWidth = clampPaneWidth(
          startWidth + moveEvent.clientX - startX,
          minWidth,
          maxWidth
        );
        setPaneWidths(current => ({
          ...current,
          primary: nextWidth,
        }));
      };
      const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp, { once: true });
    },
    [maxWidth, minWidth, paneWidths.primary, setPaneWidths]
  );

  return {
    primaryPaneWidth: paneWidths.primary,
    startPrimaryPaneResize,
  } as const;
}

export interface NativeWorkspaceAppAction {
  disabled?: boolean;
  hint?: ReactNode;
  icon?: ReactNode;
  id: string;
  onClick?: () => void;
  title: ReactNode;
  variant?: 'primary' | 'secondary';
}

export function NativeWorkspaceAppActionList({
  actions,
  compact = false,
  standalone = false,
}: {
  actions: NativeWorkspaceAppAction[];
  compact?: boolean;
  standalone?: boolean;
}): ReactElement {
  return (
    <div
      className={clsx(
        styles.welcomeActions,
        compact && styles.welcomeActionsCompact,
        standalone && styles.welcomeActionsStandalone
      )}
    >
      {actions.map(action => (
        <button
          key={action.id}
          className={clsx(
            styles.welcomeAction,
            compact && styles.welcomeActionCompact,
            action.variant === 'primary' && styles.welcomeActionPrimary
          )}
          type="button"
          disabled={action.disabled}
          onClick={action.onClick}
        >
          {action.icon ? (
            <span
              className={clsx(
                styles.welcomeActionIcon,
                action.variant === 'secondary' &&
                  styles.welcomeActionIconSecondary
              )}
            >
              {action.icon}
            </span>
          ) : null}
          <span className={styles.welcomeActionText}>
            <span className={styles.welcomeActionTitle}>{action.title}</span>
            {action.hint ? (
              <span
                className={clsx(
                  styles.welcomeActionHint,
                  compact && styles.welcomeActionHintCompact
                )}
              >
                {action.hint}
              </span>
            ) : null}
          </span>
        </button>
      ))}
    </div>
  );
}

export function NativeWorkspaceAppHeader({
  children,
  collapsed = false,
  icon,
  leading,
  title,
}: {
  children?: ReactNode;
  collapsed?: boolean;
  icon?: ReactNode;
  leading?: ReactNode;
  title: ReactNode;
}): ReactElement {
  return (
    <ViewHeader>
      <div
        className={styles.headerBar}
        data-native-workspace-app-header-collapsed={
          collapsed ? 'true' : undefined
        }
      >
        {leading}
        {icon ? <span className={styles.headerIcon}>{icon}</span> : null}
        <div className={styles.headerTitle}>{title}</div>
        {children}
      </div>
    </ViewHeader>
  );
}

export function NativeWorkspaceAppHeaderSpacer(): ReactElement {
  return <div className={styles.headerSpacer} />;
}

export function NativeWorkspaceAppHeaderToolbar({
  children,
}: {
  children?: ReactNode;
}): ReactElement {
  return <div className={styles.headerToolbar}>{children}</div>;
}

export function NativeWorkspaceAppBody({
  children,
  testId,
}: {
  children?: ReactNode;
  testId?: string;
}): ReactElement {
  return (
    <ViewBody>
      <div className={styles.bodyFill} data-testid={testId}>
        {children}
      </div>
    </ViewBody>
  );
}

export function NativeWorkspaceAppShell({
  children,
}: {
  children?: ReactNode;
}): ReactElement {
  return <div className={styles.appShell}>{children}</div>;
}

export function NativeWorkspaceAppSurface({
  children,
  className,
  testId,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  testId?: string;
}): ReactElement {
  return (
    <ViewBody>
      <div
        className={clsx(styles.surface, className)}
        data-testid={testId}
        {...props}
      >
        {children}
      </div>
    </ViewBody>
  );
}

export function NativeWorkspaceAppContentFill({
  children,
  className,
  testId,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  testId?: string;
}): ReactElement {
  return (
    <div
      className={clsx(styles.contentFill, className)}
      data-testid={testId}
      {...props}
    >
      {children}
    </div>
  );
}

export function NativeWorkspaceAppCollapsibleSidebar({
  children,
  collapseLabel = 'Close sidebar',
  expandLabel = 'Open sidebar',
  onClose,
  onOpen,
  onResizePointerDown,
  open = true,
  resizeLabel = 'Resize sidebar',
}: {
  children: ReactNode;
  collapseLabel?: string;
  expandLabel?: string;
  onClose?: () => void;
  onOpen?: () => void;
  onResizePointerDown?: PointerEventHandler<HTMLDivElement>;
  open?: boolean;
  resizeLabel?: string;
}): ReactElement {
  return (
    <Fragment>
      <NativeWorkspaceAppResponsiveSidebar
        className={styles.twoPaneResponsiveSidebar}
        open={open}
        style={{
          backgroundColor:
            'var(--streamlain-v2-layer-background-appChrome, var(--streamlain-background-primary-color))',
        }}
      >
        {children}
      </NativeWorkspaceAppResponsiveSidebar>
      {open && onClose ? (
        <WorkspaceAppSidebarToggleButton
          className={styles.twoPaneStaticCollapseButton}
          direction="collapse"
          label={collapseLabel}
          onClick={onClose}
        />
      ) : null}
      {onOpen ? (
        <WorkspaceAppSidebarToggleButton
          className={styles.twoPaneCollapsedSidebarRail}
          direction="expand"
          label={expandLabel}
          onClick={onOpen}
          open={open}
          testId="native-workspace-app-sidebar-collapsed-rail"
        />
      ) : null}
      {open && onResizePointerDown ? (
        <NativeWorkspaceAppPaneResizeHandle
          className={styles.twoPaneResizeHandle}
          label={resizeLabel}
          onPointerDown={onResizePointerDown}
        />
      ) : (
        <div
          className={clsx(styles.divider, styles.twoPaneDivider)}
          data-open={open}
        />
      )}
    </Fragment>
  );
}

export function NativeWorkspaceAppTwoPaneLayout({
  main,
  onSidebarClose,
  onSidebarOpen,
  onSidebarResizePointerDown,
  sidebar,
  sidebarCollapseLabel = 'Close sidebar',
  sidebarExpandLabel = 'Open sidebar',
  sidebarOpen = true,
  sidebarResizeLabel = 'Resize sidebar',
  sidebarWidth,
}: {
  main: ReactNode;
  onSidebarClose?: () => void;
  onSidebarOpen?: () => void;
  onSidebarResizePointerDown?: PointerEventHandler<HTMLDivElement>;
  sidebar: ReactNode;
  sidebarCollapseLabel?: string;
  sidebarExpandLabel?: string;
  sidebarOpen?: boolean;
  sidebarResizeLabel?: string;
  sidebarWidth?: number;
}): ReactElement {
  const [sidebarResizing, setSidebarResizing] = useState(false);
  const [sidebarTransitioning, setSidebarTransitioning] = useState(false);
  const resizeSettleTimerRef = useRef<number | undefined>(undefined);
  const sidebarTransitionTimerRef = useRef<number | undefined>(undefined);

  const finishSidebarTransition = useCallback(() => {
    window.clearTimeout(sidebarTransitionTimerRef.current);
    sidebarTransitionTimerRef.current = undefined;
    setSidebarTransitioning(false);
  }, []);

  const beginSidebarTransition = useCallback(() => {
    window.clearTimeout(sidebarTransitionTimerRef.current);
    setSidebarTransitioning(true);
    sidebarTransitionTimerRef.current = window.setTimeout(
      finishSidebarTransition,
      NATIVE_WORKSPACE_APP_SIDEBAR_TRANSITION_MS + 50
    );
  }, [finishSidebarTransition]);

  useEffect(
    () => () => window.clearTimeout(sidebarTransitionTimerRef.current),
    []
  );

  const handleSidebarClose = useCallback(() => {
    beginSidebarTransition();
    onSidebarClose?.();
  }, [beginSidebarTransition, onSidebarClose]);

  const handleSidebarOpen = useCallback(() => {
    beginSidebarTransition();
    onSidebarOpen?.();
  }, [beginSidebarTransition, onSidebarOpen]);

  const handleSidebarTransitionEnd = useCallback<
    TransitionEventHandler<HTMLElement>
  >(
    event => {
      if (
        event.target === event.currentTarget &&
        event.propertyName === 'grid-template-columns'
      ) {
        finishSidebarTransition();
      }
    },
    [finishSidebarTransition]
  );

  useEffect(() => {
    if (!sidebarResizing) return;

    const stopResizing = () => {
      window.clearTimeout(resizeSettleTimerRef.current);
      resizeSettleTimerRef.current = window.setTimeout(() => {
        resizeSettleTimerRef.current = undefined;
        setSidebarResizing(false);
      }, NATIVE_WORKSPACE_APP_RESIZE_SETTLE_MS);
    };
    window.addEventListener('pointerup', stopResizing, { once: true });
    window.addEventListener('pointercancel', stopResizing, { once: true });
    return () => {
      window.removeEventListener('pointerup', stopResizing);
      window.removeEventListener('pointercancel', stopResizing);
      window.clearTimeout(resizeSettleTimerRef.current);
    };
  }, [sidebarResizing]);

  const handleSidebarResizePointerDown = useCallback<
    PointerEventHandler<HTMLDivElement>
  >(
    event => {
      window.clearTimeout(resizeSettleTimerRef.current);
      setSidebarResizing(true);
      onSidebarResizePointerDown?.(event);
    },
    [onSidebarResizePointerDown]
  );

  const style =
    sidebarWidth === undefined
      ? undefined
      : ({
          '--native-app-sidebar-width': `${sidebarWidth}px`,
        } as CSSProperties);

  return (
    <main
      className={styles.twoPaneLayout}
      data-sidebar-open={sidebarOpen}
      data-sidebar-resizing={sidebarResizing}
      data-sidebar-transitioning={sidebarTransitioning}
      onTransitionEnd={handleSidebarTransitionEnd}
      style={style}
    >
      <NativeWorkspaceAppCollapsibleSidebar
        collapseLabel={sidebarCollapseLabel}
        expandLabel={sidebarExpandLabel}
        onClose={onSidebarClose ? handleSidebarClose : undefined}
        onOpen={onSidebarOpen ? handleSidebarOpen : undefined}
        onResizePointerDown={handleSidebarResizePointerDown}
        open={sidebarOpen}
        resizeLabel={sidebarResizeLabel}
      >
        {sidebar}
      </NativeWorkspaceAppCollapsibleSidebar>
      <section className={clsx(styles.mainPane, styles.twoPaneMainPane)}>
        {main}
      </section>
    </main>
  );
}

export function NativeWorkspaceAppThreePaneLayout({
  children,
  className,
  compactDetailOpen = false,
  focused = false,
  sidebarOpen = true,
  sidebarResizing = false,
  style,
  testId,
}: {
  children?: ReactNode;
  className?: string;
  compactDetailOpen?: boolean;
  focused?: boolean;
  sidebarOpen?: boolean;
  sidebarResizing?: boolean;
  style?: CSSProperties;
  testId?: string;
}): ReactElement {
  return (
    <main
      className={clsx(
        styles.threePaneLayout,
        focused && styles.threePaneLayoutFocused,
        className
      )}
      data-sidebar-open={sidebarOpen}
      data-sidebar-resizing={sidebarResizing}
      data-compact-detail-open={compactDetailOpen}
      data-focused={focused}
      data-testid={testId}
      style={style}
    >
      {children}
    </main>
  );
}

function NativeWorkspaceAppThreePaneSlot({
  children,
  className,
  inactive = false,
}: {
  children?: ReactNode;
  className: string;
  inactive?: boolean;
}): ReactElement {
  return (
    <div
      aria-hidden={inactive ? true : undefined}
      className={className}
      inert={inactive ? true : undefined}
    >
      {children}
    </div>
  );
}

export function NativeWorkspaceAppThreePanePrimary({
  children,
  inactive = false,
}: {
  children?: ReactNode;
  inactive?: boolean;
}): ReactElement {
  return (
    <NativeWorkspaceAppThreePaneSlot
      className={styles.threePanePrimaryPane}
      inactive={inactive}
    >
      {children}
    </NativeWorkspaceAppThreePaneSlot>
  );
}

export function NativeWorkspaceAppThreePaneDetail({
  children,
  inactive = false,
}: {
  children?: ReactNode;
  inactive?: boolean;
}): ReactElement {
  return (
    <NativeWorkspaceAppThreePaneSlot
      className={styles.threePaneDetailPane}
      inactive={inactive}
    >
      {children}
    </NativeWorkspaceAppThreePaneSlot>
  );
}

export function NativeWorkspaceAppResponsiveSidebar({
  children,
  className,
  open = true,
  style,
}: {
  children?: ReactNode;
  className?: string;
  open?: boolean;
  style?: CSSProperties;
}): ReactElement {
  return (
    <div
      className={clsx(styles.responsiveSidebar, className)}
      data-open={open}
      style={style}
    >
      {children}
    </div>
  );
}

export function NativeWorkspaceAppPane({
  as = 'section',
  children,
  className,
  variant = 'primary',
}: {
  as?: 'aside' | 'div' | 'section';
  children?: ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary';
}): ReactElement {
  const paneClassName = clsx(
    styles.pane,
    variant === 'secondary' && styles.paneSecondary,
    className
  );

  if (as === 'aside') {
    return <aside className={paneClassName}>{children}</aside>;
  }
  if (as === 'div') {
    return <div className={paneClassName}>{children}</div>;
  }
  return <section className={paneClassName}>{children}</section>;
}

export function NativeWorkspaceAppPaneHeader({
  action,
  children,
  closeLabel = 'Close',
  onClose,
}: {
  action?: ReactNode;
  children?: ReactNode;
  closeLabel?: string;
  onClose?: () => void;
}): ReactElement {
  return (
    <div className={styles.paneHeader}>
      {children ? <span className={styles.paneTitle}>{children}</span> : null}
      {action || onClose ? (
        <span className={styles.paneHeaderActions}>
          {action}
          {onClose ? (
            <button
              aria-label={closeLabel}
              className={styles.paneCloseButton}
              onClick={onClose}
              type="button"
            >
              <CloseIcon />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

export function NativeWorkspaceAppSidebarStatusOverlay({
  children,
}: {
  children?: ReactNode;
}): ReactElement | null {
  if (!children) return null;

  return <div className={styles.sidebarStatusOverlay}>{children}</div>;
}

export const NativeWorkspaceAppScrollPane = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    children?: ReactNode;
  }
>(function NativeWorkspaceAppScrollPane(
  { children, className, ...props },
  ref
): ReactElement {
  return (
    <div ref={ref} className={clsx(styles.scrollPane, className)} {...props}>
      {children}
    </div>
  );
});

export function NativeWorkspaceAppPaneResizeHandle({
  className,
  hiddenOnCompact = false,
  hiddenOnMobile = false,
  label,
  onPointerDown,
}: {
  className?: string;
  hiddenOnCompact?: boolean;
  hiddenOnMobile?: boolean;
  label: string;
  onPointerDown: PointerEventHandler<HTMLDivElement>;
}): ReactElement {
  return (
    <div
      className={clsx(
        styles.paneResizeHandle,
        hiddenOnCompact && styles.paneResizeHandleHiddenOnCompact,
        hiddenOnMobile && styles.paneResizeHandleHiddenOnMobile,
        className
      )}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      onPointerDown={onPointerDown}
    />
  );
}

export function NativeWorkspaceAppSidebar({
  action,
  children,
  closeLabel,
  contentClassName,
  contentPadding = 'default',
  contentProps,
  header,
  onClose,
  status,
  title,
}: {
  action?: ReactNode;
  children?: ReactNode;
  closeLabel?: string;
  contentClassName?: string;
  contentPadding?: 'default' | 'none';
  contentProps?: Omit<ComponentProps<'div'>, 'children' | 'className'>;
  header?: ReactNode;
  onClose?: () => void;
  status?: ReactNode;
  title?: ReactNode;
}): ReactElement {
  return (
    <WorkspaceAppSidebarFrame
      className={styles.sidebar}
      contentClassName={clsx(
        styles.scrollPane,
        contentPadding === 'none' && styles.scrollPaneNoPadding,
        contentClassName
      )}
      contentProps={contentProps}
      header={
        header ?? (
          <NativeWorkspaceAppPaneHeader
            action={action}
            closeLabel={closeLabel}
            onClose={onClose}
          >
            {title}
          </NativeWorkspaceAppPaneHeader>
        )
      }
      status={
        <NativeWorkspaceAppSidebarStatusOverlay>
          {status}
        </NativeWorkspaceAppSidebarStatusOverlay>
      }
    >
      {children}
    </WorkspaceAppSidebarFrame>
  );
}

export function NativeWorkspaceAppSidebarItem({
  active,
  badge,
  color,
  icon,
  meta,
  onClick,
  title,
}: {
  active?: boolean;
  badge?: ReactNode;
  color?: string | null;
  icon?: ReactNode;
  meta?: ReactNode;
  onClick?: () => void;
  title: ReactNode;
}): ReactElement {
  return (
    <button
      className={active ? styles.sidebarItemActive : styles.sidebarItem}
      type="button"
      onClick={onClick}
    >
      <span className={styles.sidebarItemIcon}>
        {color ? (
          <span
            className={styles.sidebarItemIconColor}
            style={{ background: color }}
          />
        ) : (
          icon
        )}
      </span>
      <span className={styles.sidebarItemText}>
        <span className={styles.sidebarItemTitle}>{title}</span>
        {meta ? <span className={styles.sidebarItemMeta}>{meta}</span> : null}
      </span>
      {badge ? <span className={styles.badge}>{badge}</span> : null}
    </button>
  );
}

export function NativeWorkspaceAppEmptyState({
  actions,
  hint,
  icon,
  testId,
  title,
}: {
  actions?: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  testId?: string;
  title: ReactNode;
}): ReactElement {
  return (
    <div className={styles.emptyState} data-testid={testId}>
      {icon ? <span className={styles.emptyIcon}>{icon}</span> : null}
      <h2 className={styles.emptyTitle}>{title}</h2>
      {hint ? <p className={styles.emptyHint}>{hint}</p> : null}
      {actions}
    </div>
  );
}

export function NativeWorkspaceAppWelcome({
  actions,
  children,
  footnote,
  icon,
  subtitle,
  surface = 'primary',
  testId,
  title,
  variant = 'default',
}: {
  actions?: NativeWorkspaceAppAction[];
  children?: ReactNode;
  footnote?: ReactNode;
  icon?: ReactNode;
  subtitle?: ReactNode;
  surface?: 'appChrome' | 'primary';
  testId?: string;
  title: ReactNode;
  variant?: 'compact' | 'default';
}): ReactElement {
  const compact = variant === 'compact';

  return (
    <div
      className={clsx(styles.welcome, compact && styles.welcomeCompact)}
      data-testid={testId}
    >
      <section
        className={clsx(
          styles.welcomeCard,
          surface === 'appChrome' && styles.welcomeCardAppChrome,
          compact && styles.welcomeCardCompact
        )}
      >
        <header
          className={clsx(
            styles.welcomeHeader,
            icon && styles.welcomeHeaderWithIcon,
            compact && styles.welcomeHeaderCompact
          )}
        >
          {icon ? (
            <span
              className={clsx(
                styles.welcomeIcon,
                compact && styles.welcomeIconCompact
              )}
            >
              {icon}
            </span>
          ) : null}
          <div className={styles.welcomeHeaderText}>
            <h1
              className={clsx(
                styles.welcomeTitle,
                compact && styles.welcomeTitleCompact
              )}
            >
              {title}
            </h1>
            {subtitle ? (
              <p
                className={clsx(
                  styles.welcomeSubtitle,
                  compact && styles.welcomeSubtitleCompact
                )}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
        </header>
        {actions?.length ? (
          <NativeWorkspaceAppActionList actions={actions} compact={compact} />
        ) : null}
        {children ? (
          <div className={styles.welcomeContent}>{children}</div>
        ) : null}
        {footnote ? <p className={styles.welcomeFootnote}>{footnote}</p> : null}
      </section>
    </div>
  );
}

export const nativeWorkspaceAppClasses = styles;
